import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as LocationManagementActions from './location-management.actions';
import { TLocationManagementActionType } from './location-management.models';
import { FactoryFacade } from '../factory/factory.facade';
import { LocationManagementFacade } from './location-management.facade';
import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { TREQUEST_ASSIGN_NEEDLE_STORES } from '@monorepo/types';

@Injectable({ providedIn: 'root' })
export class LocationListEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private factoryFacade: FactoryFacade,
    private locationManagementFacade: LocationManagementFacade,
    private APIHelper: AppNotificationUtilClass,
  ) {}

  getLocationList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLocationManagementActionType.GetLocationList),
      switchMap((action: LocationManagementActions.GetLocationListInfo) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        this.locationManagementFacade.getPagination().pipe(
          take(1),
          map((pagination) => {
            return [...args, pagination] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId, pagination]) =>
        this.mmDataService
          .fetchLocationListData({
            factoryId,
            page: pagination.pageNo,
            itemsPerPage: pagination.pageSize,
            selectedFilters: action.selectedFilters,
            ...(action.selectedSorting ? { selectedSorting: action.selectedSorting } : {}),
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map((res) => new LocationManagementActions.GetLocationListInfoSuccess(res, action.fetchReason)),
            catchError(() => {
              return of(new LocationManagementActions.GetLocationListInfoFailure());
            }),
          ),
      ),
    ),
  );

  getFloatingAreaFloorsForLocationDeletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLocationManagementActionType.GetFloatingAreaFloorsForLocationDeletion),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          map((factoryId) => {
            return [factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, factoryId]) =>
        from(this.mmDataService.getFloatingAreaFloorsForLocationDeletion(factoryId)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LocationManagementActions.GetFloatingAreaFloorsForLocationDeletionSuccess(res);
          }),
          catchError(() => {
            return of(new LocationManagementActions.GetFloatingAreaFloorsForLocationDeletionFailure());
          }),
        ),
      ),
    ),
  );

  deleteLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLocationManagementActionType.DeleteLocations),
      switchMap((action: LocationManagementActions.DeleteLocations) =>
        this.factoryFacade.getFactoryId().pipe(
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        from(
          this.mmDataService.deleteLocations({
            factoryId,
            locationIds: action.payload.locationIds,
            floatingAreaLocationId: action.payload.floatingAreaLocationId,
          }),
        ).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.locationManagementFacade.resetLocationInfo('ACTION');
            return new LocationManagementActions.DeleteLocationsSuccess();
          }),
          catchError(() => {
            return of(new LocationManagementActions.DeleteLocationsFailure());
          }),
        ),
      ),
    ),
  );

  fetchAssignEmployeeDropdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLocationManagementActionType.AssignEmployeeDropdown),
      switchMap((action: LocationManagementActions.GetAssignEmployeeDropdown) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.fetchAssignEmployeeDropdownData(action.locationIds)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LocationManagementActions.GetAssignEmployeeDropdownSuccess(res)),
          catchError(() => {
            return of(new LocationManagementActions.GetAssignEmployeeDropdownFailure());
          }),
        ),
      ),
    ),
  );

  assignEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLocationManagementActionType.AssignEmployee),
      switchMap((action: LocationManagementActions.AssignEmployee) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.assignEmployee(action.payload)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.locationManagementFacade.resetLocationInfo('ACTION');
            return new LocationManagementActions.AssignEmployeeSuccess();
          }),
          catchError(() => {
            return of(new LocationManagementActions.AssignEmployeeFailure());
          }),
        ),
      ),
    ),
  );

  getLocalNeedleStores$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLocationManagementActionType.GetLocalNeedleStores),
      switchMap(() =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return loaderEl;
          }),
        ),
      ),
      switchMap((loaderEl) =>
        from(this.mmDataService.getLocalNeedleStores()).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new LocationManagementActions.GetLocalNeedleStoresSuccess(res);
          }),
          catchError(() => {
            return of(new LocationManagementActions.GetLocalNeedleStoresFailure());
          }),
        ),
      ),
    ),
  );

  assignNeedleStores$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLocationManagementActionType.AssignNeedleStores),
      switchMap((action: LocationManagementActions.AssignNeedleStores) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        from(this.mmDataService.assignNeedleStores(action.payload as TREQUEST_ASSIGN_NEEDLE_STORES)).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            this.locationManagementFacade.resetLocationInfo('ACTION');
            return new LocationManagementActions.AssignNeedleStoresSuccess();
          }),
          catchError(() => {
            return of(new LocationManagementActions.AssignNeedleStoresFailure());
          }),
        ),
      ),
    ),
  );
}
